import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { notifications } from "@mantine/notifications";
import {
  tasksService,
  CreateTaskInput,
  UpdateTaskInput,
} from "../../services/admin/tasks.service";

export function useTasksAdmin() {
  return useQuery({
    queryKey: ["admin", "tasks"],
    queryFn: async () => {
      const result = await tasksService.getAll();
      return result.tasks;
    },
  });
}

export function useActiveTasks() {
  return useQuery({
    queryKey: ["tasks", "active"],
    queryFn: async () => {
      const result = await tasksService.getActive();
      return result.tasks;
    },
  });
}

export function useCreateTask() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: CreateTaskInput) => tasksService.create(input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin", "tasks"] });
      queryClient.invalidateQueries({ queryKey: ["tasks"] });
      notifications.show({
        title: "Success",
        message: "Task created successfully",
        color: "green",
      });
    },
    onError: (error: any) => {
      notifications.show({
        title: "Error",
        message: error.response?.data?.error || "Failed to create task",
        color: "red",
      });
    },
  });
}

export function useUpdateTask() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: UpdateTaskInput }) =>
      tasksService.update(id, input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin", "tasks"] });
      queryClient.invalidateQueries({ queryKey: ["tasks"] });
      notifications.show({
        title: "Success",
        message: "Task updated successfully",
        color: "green",
      });
    },
    onError: (error: any) => {
      notifications.show({
        title: "Error",
        message: error.response?.data?.error || "Failed to update task",
        color: "red",
      });
    },
  });
}

export function useDeactivateTask() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => tasksService.deactivate(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin", "tasks"] });
      queryClient.invalidateQueries({ queryKey: ["tasks"] });
      notifications.show({
        title: "Success",
        message: "Task deactivated successfully",
        color: "green",
      });
    },
    onError: (error: any) => {
      notifications.show({
        title: "Error",
        message: error.response?.data?.error || "Failed to deactivate task",
        color: "red",
      });
    },
  });
}
